import axios from 'axios';
import { API_URL } from '../config';
import { supabase } from '../config/SupabaseClient';

const instance = axios.create({
  baseURL: API_URL,
  // baseURL: 'https://interfaz.avanzaempleo.com.co',
  httpsAgent: {
    rejectUnauthorized: false,
  },
});

// Interceptor de request para agregar JWT de Supabase y validar IDs
instance.interceptors.request.use(
  async (config) => {
    // Agregar token JWT de Supabase si existe sesión activa
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch (error) {
      console.warn('No fue posible obtener el token de Supabase:', error);
    }

    // Verificar si la URL contiene "null" o "undefined" como parte del path
    const url = config.url || '';
    if (url.includes('/null') || url.includes('/undefined') || url.includes('null/') || url.includes('undefined/')) {
      const error = new Error('Invalid ID in request URL');
      error.name = 'InvalidIdError';
      error.skipRequest = true;
      return Promise.reject(error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const notifyAuthBlock = (errorId) => {
  localStorage.setItem("errId", String(errorId));
  const event = new CustomEvent("auth-error", { detail: errorId });
  window.dispatchEvent(event);
};

// Interceptor de response para manejar errores de autenticación
let lastAuthBlock = null;
const notifyAuthBlockOnce = (value) => {
  if (lastAuthBlock === value) return;
  lastAuthBlock = value;
  localStorage.setItem("errId", String(value));
  window.dispatchEvent(new CustomEvent("auth-error", { detail: value }));
};

instance.interceptors.response.use(
  (response) => {
    notifyAuthBlockOnce(0);
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const details = error.response.data?.detail;

    if (status === 401) {
      console.warn('Unauthorized: Token expirado o inválido');
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        console.error('Error en logout:', signOutError);
      }
      localStorage.clear(); 
      window.location.href = '/';
    }

    if (status === 403 && details === "Usuario baneado") {
      notifyAuthBlock(1);
    }

    return Promise.reject(error);
  }
);

/**
 * Valida si un ID es válido para hacer peticiones HTTP
 * @param {*} id - El ID a validar
 * @returns {boolean} - true si el ID es válido, false en caso contrario
 */
const isValidId = (id) => {
  // Validación estricta: null, undefined, o strings inválidos
  if (id === null || id === undefined) return false;
  
  // Convertir a string y validar
  const idString = String(id).trim();
  
  // Rechazar strings vacíos, "null", "undefined", o que contengan solo espacios
  if (idString === '' || 
      idString === 'null' || 
      idString === 'undefined' || 
      idString === 'NaN' ||
      idString.toLowerCase() === 'null' ||
      idString.toLowerCase() === 'undefined') {
    return false;
  }
  
  // Validar que no sea un número inválido
  if (!isNaN(idString) && (parseInt(idString) <= 0 || !isFinite(idString))) {
    return false;
  }
  
  return true;
};

/**
 * Valida y sanitiza un ID antes de usarlo en una petición HTTP
 * @param {*} id - El ID a validar
 * @param {string} entityType - Tipo de entidad para el mensaje de error
 * @returns {string} - ID validado y sanitizado
 * @throws {Error} - Si el ID es inválido
 */
const validateAndSanitizeId = (id, entityType) => {
  if (!isValidId(id)) {
    const error = new Error(`${entityType} ID is required`);
    error.name = 'InvalidIdError';
    error.skipRequest = true;
    throw error;
  }
  return String(id).trim();
};

export const getUsers = async (id) => {
  try {
    const response = await instance.get(`/users/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getLogin = async (email,password) => {
  try {
    const response = await instance.get(`/login/${email}/${password}`);
    
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Registra un nuevo usuario en la base de datos antigua con el UUID de Supabase
 * Se utiliza después de crear el usuario en Supabase
 * @param {string} supabase_uid - UUID del usuario creado en Supabase
 * @param {string} username - Nombre de usuario
 * @param {string} email - Email del usuario
 * @param {number} role_id - Rol del usuario (1=empresa, 2=candidato)
 * @returns {Promise<Object>} - Datos del usuario registrado
 */
export const registerUser = async (username, email, role_id, password) => {
  const numericRoleId = parseInt(role_id, 10);
  if (numericRoleId !== 1 && numericRoleId !== 2) {
    throw new Error('Invalid role_id. Must be 1 (empresa) or 2 (candidato).');
  }

  try {
    const response = await instance.post('/users', {
      username,
      email,
      password,
      role_id: numericRoleId,
    });
    
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.detail || error.message;
    console.error('Error en registro:', errorMessage);
    throw new Error(errorMessage);
  }
};

// Se encarga de conectar el id que se obtiene del login de supabase con la base de datos antigua, para obtener el rol y demás datos del usuario.
export const getUserInfo = async () => {

  try {
    const response = await instance.get('/users/me');
    if (response.data.status_code === 404) {
      localStorage.clear()
      throw new Error('Error en el login - Usuario no encontrado en la base de datos avanza');
    }
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getEmployeeAdmin = async (searchTerm) => {
  try {
    if (searchTerm) {
      const response = await instance.get(`/admin/employees?${searchTerm}`);
      return response.data;
    } else {
      const response = await instance.get(`/admin/employees`);
      return response.data;
    }
    
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getCompanyAdmin = async (searchTerm) => {
  try {
    if (searchTerm) {
      const response = await instance.get(`/admin/companies?${searchTerm}`);
      return response.data;
    } else {
      const response = await instance.get(`/admin/employees`);
      return response.data;
    }
    
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getOfferAdmin = async (searchTerm) => {
  try {
    if (searchTerm) {
      const response = await instance.get(`/admin/job_offers?${searchTerm}`);
      return response.data;
    } else {
      const response = await instance.get(`/admin/job_offers`);
      return response.data;
    }
    
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getUsersAdmin = async (searchTerm) => {
  try {
    if (searchTerm) {
      const response = await instance.get(`/admin/users?${searchTerm}`);
      return response.data;
    } else {
      const response = await instance.get(`/admin/users`);
      return response.data;
    }
    
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getAdminStats = async () => {
  try {
    const response = await instance.get(`/admin/stats`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getEmployeeById = async (id) => {
  try {
    const response = await instance.get(`/admin/employees/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export const updateEmployee = async (id, rawData) => {
  // Limpiamos el objeto para que coincida con el esquema
  const formattedData = {
    name: String(rawData.name || "").trim(),
    lastname: String(rawData.lastname || "".trim()),
    phone: String(rawData.phone || "").trim(),
    description: String(rawData.description || "").trim(),
    salary: String(rawData.salary || "").trim(),
    country: String(rawData.country || "").trim(),
    city: String(rawData.city || "").trim(),
    department: String(rawData.department || "").trim(),
    adress: String(rawData.adress || "").trim(),
    identificationNumber: String(rawData.identificationNumber || "").trim(),
    typeIdentification: String(rawData.typeIdentification || "").trim(),
    sexo: String(rawData.sexo || "").trim(),
    nationality: String(rawData.nationality || "").trim(),
    studyLevel: String(rawData.studyLevel || "").trim(),
    birthDate: String(rawData.birthDate || "").trim(),
    neighborhood: String(rawData.neighborhood || "").trim(),
    languaje: String(rawData.languaje || "").trim(),
    first_job: Boolean(rawData.first_job)
  };

  try {
    const response = await instance.patch(`/admin/employees/${id}`, formattedData);
    return response.data;
  } catch (error) {
    console.error("Error en PATCH:", error.response?.data || error.message);
    throw error;
  }
};

export const getCompanyById = async (id) => {
  try {
    const response = await instance.get(`/admin/companies/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export const updateCompany = async (id, rawData) => {
  const formattedData = {
    name: String(rawData.name || "").trim(),
    country: String(rawData.country || "").trim(),
    city: String(rawData.city || "").trim(),
    department: String(rawData.department || "").trim(),
    adress: String(rawData.adress || "").trim(),
    phone: String(rawData.phone || "").trim(),
    description: String(rawData.description || "").trim(),
    
    nit: String(rawData.nit || "").trim(),
    check_digit: String(rawData.check_digit || "").trim(),
    
    contact_name: String(rawData.contact_name || "").trim(),
    contact_position: String(rawData.contact_position || "").trim(),
    contact_number: String(rawData.contact_number || "").trim(),
    
    verified: Boolean(rawData.verified),
    
    monthly_credit_limit: Number(rawData.monthly_credit_limit || 0),
    active_offers_limit: Number(rawData.active_offers_limit || 0),
    available_credits: Number(rawData.available_credits || 0)
  };

  try {
    const response = await instance.patch(`/admin/companies/${id}`, formattedData);
    return response.data;
  } catch (error) {
    console.error("Error en PATCH:", error.response?.data || error.message);
    throw error;
  }
};


export const getOfferById = async (id) => {
  try {
    const response = await instance.get(`/admin/job_offers/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export const updateOffer = async (id, rawData) => {
  const datosOferta = rawData.job_offer;
  const formattedData = {
    title: String(datosOferta.title || "").trim(),
    description: String(datosOferta.description || "").trim(),
    modality: String(datosOferta.modality || "").trim(),
    experience: String(datosOferta.experience || "").trim(),
    city: String(datosOferta.city || "").trim(),
    department: String(datosOferta.department || "").trim(),
    study_level: String(datosOferta.study_level || "").trim(),
    contract_type: String(datosOferta.contract_type || "").trim(),
    available_time: String(datosOferta.available_time || "").trim(),
    
    languaje: String(datosOferta.languaje || "").trim(),
    languaje_level: String(datosOferta.languaje_level || "").trim(),

    salary: Number(datosOferta.salary || 0),

    date: datosOferta.date ? new Date(datosOferta.date).toISOString().split('T')[0] : "",

    required_vehicle: Boolean(datosOferta.required_vehicle),
    status: Boolean(datosOferta.status),
    suspended: Boolean(datosOferta.suspended),
  };

  try {
    const response = await instance.patch(`/admin/job_offers/${id}`, formattedData);
    return response.data;
  } catch (error) {
    console.error("Error en PATCH:", error.response?.data || error.message);
    throw error;
  }
}

export const updateUser = async (id, userData) => {  
  try {
    if (userData.password) {
      await changeUserPassword(id, userData.password);
    }

    if (userData.banned !== undefined) {
      await toggleUserBan(id, userData.banned);
    }
    
    return { success: true, message: "Usuario actualizado correctamente" };
  } catch (error) {
    console.error("Error en el proceso de actualización:", error);
    throw error;
  }
}

export const changeUserPassword = async (userId, newPassword) => {
  try {
    const response = await instance.put(`/admin/change-password/${userId}`, {
      password: newPassword
    });
    
    console.log("Contraseña actualizada con éxito:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error al cambiar la contraseña:", error.response?.data || error.message);
    throw error;
  }
};

export const toggleUserBan = async (id, isBanned) => {
  try {
    const response = await instance.patch(`/admin/users/${id}`, { "banned": Boolean(isBanned) });
    
    return response.data;
  } catch (error) {
    console.error("Error al actualizar estado de baneo:", error);
    throw error;
  }
};

/**
 * Esta función ya no se utiliza para registrar usuarios,
 * es reemplazada por registerUserToDatabase que se integra con Supabase.
 * Se deja por si se necesita para alguna funcionalidad específica en el futuro, pero no es recomendable usarla para registro de usuarios.
export const postUser = async (user, email, password, rol) => {
  if (rol === null || rol === '' || isNaN(rol)) {
    rol = "1";
  } // Imprimirá "string"
  rol = parseInt(rol);
  try {
    const response = await instance.post('/users', {
      username: user,
      email: email,
      password: password,
      role_id: rol // Asegúrate de que esto sea un número, ya que en el JSON se muestra como 0
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Esta funcion tampoco se utiliza para actualizar la contraseña, ya que ahora se maneja a través de Supabase. 
// Se recomienda usar las funciones de recuperación de contraseña de Supabase en su lugar.
export const putUserPass = async (id, password) => {

  try {
    const response = await instance.put(`/updateUser/${id}`, {
      password: password
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
*/

export const getEmployees = async (id) => {
  // Validar y sanitizar el ID antes de hacer cualquier petición
  const validId = validateAndSanitizeId(id, 'Employee');
  
  try {
    const response = await instance.get(`/employees/${validId}`);
    return response.data;
  } catch (error) {
    // No loggear errores de ID inválido ya que se validaron antes
    if (error.name !== 'InvalidIdError') {
      console.error(error);
    }
    throw error;
  }
};

export const getEmployees3 = async (id) => {
  try {
    const response = await instance.get(`/employees2/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getEmployees2 = async () => {
  try {
    const response = await instance.get(`/employees`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};




export const postEmployees = async (employeData) => {
  
  const userId = localStorage.getItem("id");
  var rol = localStorage.getItem("rol")

  if(rol===2||rol==="2"){
    try {
        
        await instance.get(`/employees/${userId}`);
        await instance.put(`/employees/${employeData.id}`, {
            name: employeData.name, 
            lastname: employeData.lastname,
            phone: employeData.phone,
            description: employeData.description,
            salary: employeData.salary,
            country: employeData.country,
            city: employeData.city,
            department: employeData.departmen,
            adress: employeData.adress,
            photo: employeData.photo,
            user_id: userId,
            identificationNumber:employeData.identificationNumber,
            typeIdentification:employeData.typeIdentification,
            sexo:employeData.sexo,
            nationality:employeData.nationality,
            studyLevel:employeData.studyLevel,
            birthDate:employeData.birthDate,
            neighborhood:employeData.neighborhood,
            languaje: employeData.languaje,
            first_job:employeData.first_job,
        });
      // if (response.data !== null && response.data !== ''){
        
      
    } catch (error) {
      // Puedes verificar el código de estado o el mensaje de error para saber si debes actualizar
        try {
        
            await instance.post('/employees', { 
              name: employeData.name, 
              lastname: employeData.lastname,
              phone: employeData.phone,
              description: employeData.description,
              salary: employeData.salary,
              country: employeData.country,
              city: employeData.city,
              department: employeData.departmen,
              adress: employeData.adress,
              photo: employeData.photo,
              user_id: userId,
              identificationNumber:employeData.identificationNumber,
              typeIdentification:employeData.typeIdentification,
              sexo:employeData.sexo,
              nationality:employeData.nationality,
              studyLevel:employeData.studyLevel,
              birthDate:employeData.birthDate,
              neighborhood:employeData.neighborhood,
              languaje: employeData.languaje,
              first_job:employeData.first_job,

          });
        //   // Tus datos actualizados aquí
        //   return updateResponse.data;
        } catch (updateError) {
          console.error(updateError);
          throw updateError;
        }
      
    }
  }else{
    try {
      
        await instance.get(`/companies/${userId}`);
        await instance.put(`/companies/${employeData.id}`, {
          name: employeData.name, 
          phone: employeData.phone,
          description: employeData.description,
          country: employeData.country,
          city: employeData.city,
          department: employeData.departmen,
          adress: employeData.adress,
          photo: employeData.photo,
          user_id: userId,
          nit: employeData.nit ,
          check_digit: employeData.check_digit,
          contact_name: employeData.contact_name,
          contact_position: employeData.contact_position,
          contact_number: employeData.contact_number
        });
      // if (response.data !== null && response.data !== ''){
        
      
    } catch (error) {
      // Puedes verificar el código de estado o el mensaje de error para saber si debes actualizar
        try {
        
            await instance.post('/companies', { 
              name: employeData.name, 
              phone: employeData.phone,
              description: employeData.description,
              country: employeData.country,
              city: employeData.city,
              department: employeData.departmen,
              adress: employeData.adress,
              photo: employeData.photo,
              user_id: userId,
              nit: employeData.nit ,
              check_digit: employeData.check_digit,
              contact_name: employeData.contact_name,
              contact_position: employeData.contact_position,
              contact_number: employeData.contact_number

          });
        // Tus datos actualizados aquí
        //   return updateResponse.data;
        } catch (updateError) {
          console.error(updateError);
          throw updateError;
        }
      
    }
  }
};

export const postDocumento = async (name, tipo) => {
  var userId = localStorage.getItem("id");
  userId = parseInt(userId);
  try {
    const response = await instance.post('/documents', {
      name: name,
      type: tipo,
      user_id: userId
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const postDocumentoAdmin = async (name, tipo, userId) => {
  userId = parseInt(userId);
  try {
    const response = await instance.post('/documents', {
      name: name,
      type: tipo,
      user_id: userId
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getJobs = async (params = {}) => {
  try {
    const { department, city, categoryId } = params;
    const query = new URLSearchParams();
    if (department != null && department !== "") query.append("department", department);
    if (city != null && city !== "") query.append("city", city);
    if (categoryId != null && categoryId !== "") query.append("category_id", categoryId);
    const qs = query.toString();
    const url = qs ? `/job_offers?${qs}` : "/job_offers";
    const response = await instance.get(url);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getJobs_id = async (id) => {
  try {
    const response = await instance.get(`/job_offers/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getJobsEnrichedByCompany = async (id) => {
  const validId = validateAndSanitizeId(id, 'Company');
  try {
    const response = await instance.get(`/job_offers_company/${validId}`);
    return response.data;
  } catch (error) {
    if (error.name !== 'InvalidIdError') {
      console.error(error);
    }
    throw error;
  }
};

export const getJobs_id2 = async (id) => {
  try {
    const response = await instance.get(`/job_offers2/${id}`);

    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getJobOfferTools = async (id) => {
  try {
    const response = await instance.get(`/job_offer_tools/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getJobOfferApplicationsFull = async (jobOfferId, page = 1, pageSize = 10) => {
  try {
    const response = await instance.get(
      `/applications/offerApplication/${jobOfferId}/full`,
      { params: { page, page_size: pageSize } }
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const get_saved_offers_by_employee_id = async (id) => {
  try {
    const response = await instance.get(`/job_offers/saved/${id}`);

    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const get_saved_offers_by_job_employee = async (job_offer_id,employee_id) => {
  try {
    const response = await instance.get(`/job_offers/filtered/${job_offer_id}/${employee_id}`);

    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const delete_saved_offers_by_job_employee = async (job_offer_id,employee_id) => {
  try {
    const response = await instance.delete(`/job_offers/unsave/${job_offer_id}/${employee_id}`);

    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const saveJobOffer = async (jobOfferData) => {
  try {
    const response = await instance.post('/job_offers/save-job-offer', jobOfferData);
    return response.data;
  } catch (error) {
    // Manejar el error adecuadamente
    if (error.response) {
      // El servidor respondió con un estado diferente de 2xx
      console.error('Error:', error.response.data);
      
      throw new Error(error.response.data.detail);
    } else if (error.request) {
      // La solicitud se hizo pero no se recibió respuesta
      console.error('Error:', error.request);
      throw new Error('No response received from the server');
    } else {
      // Ocurrió un error al configurar la solicitud
      console.error('Error:', error.message);
      throw new Error('Error configuring the request');
    }
  }
};

export const getCategoryById = async (id) => {
  try {
    const response = await instance.get(`/categories/${id}`);

    
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const postApplications = async (employee, job_offer) => {
  console.log("emp"+employee);
  console.log("joboffer"+job_offer);
  try {
    const response = await instance.post('/applications', {
      employee_id: employee,
      job_offer_id: job_offer // Asegúrate de que esto sea un número, ya que en el JSON se muestra como 0
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getUserApplications = async (user_id) => {
  try {
    const response = await instance.get(`/applications/userApplication/${user_id}`);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getJobOfferApplications = async (job_offer_id) => {
  try {
    const response = await instance.get(`/applications/offerApplication/${job_offer_id}`);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getApplications = async () => {
  try {
    const response = await instance.get(`/applications`);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const postJobOffers = async (jobOfferData) => {
  if (isNaN(jobOfferData.category_id)||jobOfferData.category_id===''){
    jobOfferData.category_id = 1 
  } 
  try {
    const response = await instance.post('/job_offers', {
      title: jobOfferData.title,
      description: jobOfferData.description,
      salary: jobOfferData.salary,
      modality: jobOfferData.modality,
      category_id: jobOfferData.category_id,
      company_id: jobOfferData.company_id,
      experience: jobOfferData.experience,
      date: jobOfferData.date,
      city:jobOfferData.city,
      department:jobOfferData.department,
      languaje:jobOfferData.languaje,
      languaje_level: jobOfferData.languaje_level,
      required_vehicle:jobOfferData.required_vehicle,
      study_level:jobOfferData.study_level,
      contract_type:jobOfferData.contract_type,
      available_time: jobOfferData.available_time,
      technology_tools: jobOfferData.technology_tools,
      status: jobOfferData.status,
      suspended: jobOfferData.suspended
    });
    
    console.log(response);
    
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const putJobOffers = async (jobOfferData,ids) => {
 
  try {
    const response = await instance.put(`/job_offers/${ids}`, {
      title: jobOfferData.title,
      description: jobOfferData.description,
      salary: jobOfferData.salary,
      modality: jobOfferData.modality,
      category_id: jobOfferData.category_id,
      company_id: jobOfferData.company_id,
      experience: jobOfferData.experience,
      date: jobOfferData.date,
      city:jobOfferData.city,
      department:jobOfferData.department,
      languaje:jobOfferData.languaje,
      languaje_level: jobOfferData.languaje_level,
      required_vehicle:jobOfferData.required_vehicle,
      study_level:jobOfferData.study_level,
      contract_type:jobOfferData.contract_type,
      available_time: jobOfferData.available_time,
      technology_tools: jobOfferData.technology_tools,
      status: jobOfferData.status,
      suspended: jobOfferData.suspended
    });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const putJobOfferStatus = async (id,status) => {
 
  try {
    const response = await instance.put(`/job_offers/${id}/${status}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const putJobOfferSuspendedStatus = async (id,status) => {
 
  try {
    const response = await instance.put(`/job_offers/suspend/${id}/${status}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getCategory = async () => {
  try {
    const response = await instance.get(`/categories`);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getInterestsByUser = async (userId) => {
  try {
    const response = await instance.get(`/interests/user/${userId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) return [];
    console.error(error);
    throw error;
  }
};

export const deleteInterestsByUser = async (userId) => {
  try {
    await instance.delete(`/interests/user/${userId}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const postInterest = async (interest, userId) => {
  try {
    const response = await instance.post("/interests", { interest: String(interest), user_id: Number(userId) });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getCategoriesWithCount = async () => {
  try {
    const response = await instance.get(`/categories-with-count`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getJobsByCategory = async (categoryId) => {
  try {
    const response = await instance.get(`/job_offers/category/${categoryId}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getCompany = async (id) => {
  // Validar y sanitizar el ID antes de hacer cualquier petición
  const validId = validateAndSanitizeId(id, 'Company');
  
  try {
    const response = await instance.get(`/companies/${validId}`);
    return response.data;
  } catch (error) {
    // No loggear errores de ID inválido ya que se validaron antes
    if (error.name !== 'InvalidIdError') {
      console.error(error);
    }
    throw error;
  }
};

export const getCompany2 = async (id) => {
  try {
    const response = await instance.get(`/companies2/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deletejoboffer = async (id) => {
  try {
    
    const response = await instance.delete(`/job_offers/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await instance.post(`/upload/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.filename;
  } catch (error) {
    throw error;
  }
};


export const downloadFile = async (filename) => {
  try {
    const response = await instance.get(`/download/${filename}`, {
      responseType: 'blob', // Para recibir datos binarios
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDocument = async (id) => {
  try {
    const response = await instance.get(`/documents/${id}`);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deleteDocument = async (filename) => {
  try {
    const response = await instance.delete(`/delete/${filename}`);
    
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deleteDocuments = async (id) => {
  try {
    const response = await instance.delete(`/documents/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};


export const downloadFile2 = async (filename) => {  
  try {
    const response = await instance.get(`/download2/${filename}`, {
      responseType: 'blob', // Para recibir datos binarios
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const downloadFile3 = async (filename) => {  
  try {
    const response = await instance.get(`/download/${filename}`, {
      responseType: 'blob', // Para recibir datos binarios
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteImg = async (filename) => {
  try {
    const response = await instance.delete(`/delete/${filename}`);
    return response.data.filename;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const adminDeleteImg = async (filename) => {
  try {
    const response = await instance.delete(`/delete-image/${filename}`);
    return response.data.status;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const uploadFileImg = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  var ids = localStorage.getItem("id")

  try {
    const response = await instance.post(`/upload2/${ids}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data.filename;
  } catch (error) {
    throw error;
  }
};

export const adminUploadImage = async (id, file) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await instance.post(`/upload2/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};


export const adminWriteImage = async (id, type, filename) => {
  let endpoint;
  switch (type) {
  case "candidato":
    endpoint = 'employees';
    break;
  case "empresa":
    endpoint = 'companies';
    break;
  default:
    return 'Error'
}
  try {
    const response = await instance.patch(`/admin/${endpoint}/${id}`, { photo: String(filename) });
    return response.data;
  } catch (error) {
    console.error("Error en PATCH:", error.response?.data || error.message);
    throw error;
  }
};


export const getEducations = async (id) => {
  try {
    const response = await instance.get(`/educations2/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getEducation = async (id) => {
  try {
    const response = await instance.get(`/educations/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const postEducation = async (title, institution, start_date, end_date, user_id, certificate) => {
  
  try {
    const response = await instance.post('/educations', {
      title: title,
      institution: institution,
      start_date: start_date,
      end_date: end_date,
      user_id: user_id,
      certificate:certificate,
    });
    
    
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deleteEducation = async (id) => {
  try {
    const response = await instance.delete(`/educations/${id}`);
    return response.data.filename;
  } catch (error) {
    console.error(error);
    throw error;
  }
};



export const putEducation = async (title, institution, start_date, end_date, user_id, id, certificate) => {

  try {
    const response = await instance.put(`/educations/${id}`, {
      title: title,
      institution: institution,
      start_date: start_date,
      end_date: end_date,
      user_id: user_id,
      certificate:certificate,
    });
    
    
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getExperiences = async (id) => {
  try {
    const response = await instance.get(`/experiences2/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getExperience = async (id) => {
  try {
    const response = await instance.get(`/experiences/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const postExperience = async (company, post, description, start_date,end_date, user_id, jobCertificate, informalJob, ownVehicle) => {
  
  try {
    const response = await instance.post('/experiences', {
      company: company,
      post: post,
      description :  description ,
      start_date: start_date,
      end_date: end_date,
      user_id: user_id,
      certificate: jobCertificate,
      informal_job: informalJob,
      own_vehicle: ownVehicle
    });
    
    
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error('Error de servidor:', error.response.data);
      console.error('Código de estado:', error.response.status);
    } else if (error.request) {
      console.error('Error de red, no se recibió respuesta');
    } else {
      console.error('Error durante la configuración de la solicitud:', error.message);
    }
    throw error;
  }
};

export const deleteExperience = async (id) => {
  try {
    const response = await instance.delete(`/experiences/${id}`);
    return response.data.filename;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const putExperience = async  (company, post, description, start_date, end_date, user_id, id, jobCertificate, informalJob, ownVehicle) => {

  try {
    const response = await instance.put(`/experiences/${id}`, {
      company: company,
      post: post,
      description:description,
      start_date: start_date,
      end_date: end_date,
      user_id: user_id,
      certificate: jobCertificate,
      informal_job: informalJob,
      own_vehicle: ownVehicle
    });
    
    console.log("response " + response);
    
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const send_newsletter = async (user_email)=>{
  try{
    const response = await instance.get(`/send_newsletter/${user_email}`)
    return response.data;

  } catch(error){
    console.log(error)
    throw error;
  }

}

/**
 * Esta función ya no se utiliza para recuperar contraseñas, el manejo de recuperación de contraseña ahora se hace completamente con Supabase. 
 * Se deja por si se necesita para alguna funcionalidad específica en el futuro, pero no es recomendable usarla para recuperación de contraseñas.
export const forgot_pass = async (user_email)=>{
  try{
    const response = await instance.get(`/forgot_password/${user_email}`)
    return response.data;

  } catch(error){
    console.log(error)
    throw error;
  }

}
  */
